import React, { useEffect, useRef, useState } from 'react';
import { ToolbarIcon } from './icons';

// Dropdown menu primitive used by Action / View / Help
const DropdownMenu = ({ x, y, items, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-[110] bg-[#F5F5F5] border border-[#858585] py-[2px] text-[12px]"
      style={{
        left: x,
        top: y,
        minWidth: 220,
        fontFamily: 'Tahoma, Segoe UI, sans-serif',
        boxShadow: '2px 2px 6px rgba(0,0,0,0.25)',
      }}
    >
      {items.map((item, i) => {
        if (item.separator) return <div key={i} className="h-[1px] bg-[#C0C0C0] my-[2px] mx-[4px]" />;
        const handle = () => {
          if (item.disabled) return;
          if (item.onClick) item.onClick();
          onClose();
        };
        return (
          <div
            key={i}
            onClick={handle}
            className={`flex items-center h-[22px] px-[8px] cursor-default ${
              item.disabled ? 'text-[#999]' : 'text-black hover:bg-[#2B579A] hover:text-white'
            } ${item.checked ? 'font-semibold' : ''}`}
          >
            <div className="w-[16px] mr-[8px] flex items-center justify-center">
              {item.checked ? <span className="text-[12px]">{'\u2713'}</span> : (item.icon || null)}
            </div>
            <div className="flex-1">{item.label}</div>
            {item.accel && <span className="ml-[16px] text-[11px] opacity-70">{item.accel}</span>}
          </div>
        );
      })}
    </div>
  );
};

// Top menu bar (Action / View / Help) - File removed per user request
export const MenuBar = ({ menuItems, viewMode }) => {
  const [open, setOpen] = useState(null); // {label, x, y}
  const items = ['Action', 'View', 'Help'];

  const handleClick = (label, e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOpen({ label, x: r.left, y: r.bottom });
  };

  return (
    <div
      className="flex h-[20px] bg-[#F0F0F0] border-b border-[#D0D0D0] px-[2px]"
      style={{ fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
    >
      {items.map((it) => (
        <button
          key={it}
          onClick={(e) => handleClick(it, e)}
          className={`px-[8px] h-full text-[12px] text-black hover:bg-[#C1D7F0] active:bg-[#A0C0E0] cursor-default ${
            open && open.label === it ? 'bg-[#A0C0E0]' : ''
          }`}
        >
          <span><u>{it.charAt(0)}</u>{it.slice(1)}</span>
        </button>
      ))}
      {open && menuItems[open.label] && (
        <DropdownMenu
          x={open.x}
          y={open.y}
          items={menuItems[open.label]}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
};

const ToolbarBtn = ({ kind, onClick, title, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    className={`w-[22px] h-[22px] flex items-center justify-center border border-transparent ${
      disabled ? '' : 'hover:border-[#7FA9DC] hover:bg-[#DCE9F9] active:bg-[#B0CEEC]'
    }`}
  >
    <ToolbarIcon kind={kind} disabled={disabled} />
  </button>
);

const Sep = () => <div className="w-[1px] h-[18px] bg-[#C0C0C0] mx-[3px] self-center" />;

// Toolbar - with New User/Group/OU, Filter, and Help icon REMOVED per user request
export const Toolbar = ({ onBack, onForward, onUp, onRefresh, onProperties, onFind, canBack, canForward, canUp, hasSelection }) => {
  return (
    <div className="flex items-center h-[26px] bg-[#F5F5F5] border-b border-[#D0D0D0] px-[4px]">
      <ToolbarBtn kind="back" title="Back" onClick={onBack} disabled={!canBack} />
      <ToolbarBtn kind="forward" title="Forward" onClick={onForward} disabled={!canForward} />
      <Sep />
      <ToolbarBtn kind="up" title="Up one level" onClick={onUp} disabled={!canUp} />
      <Sep />
      <ToolbarBtn kind="refresh" title="Refresh (reloads page)" onClick={onRefresh} />
      <ToolbarBtn kind="properties" title="Properties" onClick={onProperties} disabled={!hasSelection} />
      <Sep />
      <ToolbarBtn kind="find" title="Find" onClick={onFind} />
    </div>
  );
};

// Top blue title bar - new title per user request
export const TitleBar = () => (
  <div
    className="flex items-center justify-between h-[28px] bg-[#2B579A] text-white px-[8px]"
    style={{ fontFamily: 'Segoe UI, sans-serif' }}
  >
    <div className="flex items-center min-w-0">
      <div className="mr-[8px] flex-shrink-0">
        <svg width="14" height="14" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="7" fill="#FFFFFF" stroke="#1F4070" strokeWidth="0.5" />
          <rect x="5" y="2" width="6" height="4" fill="#2B579A" />
          <rect x="1" y="10" width="5" height="3" fill="#2B579A" />
          <rect x="10" y="10" width="5" height="3" fill="#2B579A" />
          <line x1="8" y1="6" x2="3" y2="10" stroke="#2B579A" strokeWidth="0.5" />
          <line x1="8" y1="6" x2="13" y2="10" stroke="#2B579A" strokeWidth="0.5" />
        </svg>
      </div>
      <span className="text-[12.5px] truncate">Phillip Justus &mdash; Professional Profiles and Areas of Expertise</span>
    </div>
    <div className="flex items-center gap-[2px] flex-shrink-0">
      <button className="w-[40px] h-[28px] hover:bg-[#3C6BAC] flex items-center justify-center" title="Minimize">
        <svg width="10" height="10" viewBox="0 0 10 10"><line x1="1" y1="8" x2="9" y2="8" stroke="#FFF" strokeWidth="1.2" /></svg>
      </button>
      <button className="w-[40px] h-[28px] hover:bg-[#3C6BAC] flex items-center justify-center" title="Maximize">
        <svg width="10" height="10" viewBox="0 0 10 10"><rect x="1" y="1" width="8" height="8" fill="none" stroke="#FFF" strokeWidth="1" /></svg>
      </button>
      <button className="w-[40px] h-[28px] hover:bg-[#E81123] flex items-center justify-center" title="Close">
        <svg width="10" height="10" viewBox="0 0 10 10">
          <line x1="1" y1="1" x2="9" y2="9" stroke="#FFF" strokeWidth="1.2" />
          <line x1="9" y1="1" x2="1" y2="9" stroke="#FFF" strokeWidth="1.2" />
        </svg>
      </button>
    </div>
  </div>
);

export const StatusBar = ({ count, selectedCount }) => (
  <div
    className="flex items-center h-[22px] bg-[#F0F0F0] border-t border-[#D0D0D0] text-[11px] text-black px-[8px]"
    style={{ fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
  >
    <span>{selectedCount > 0 ? `${selectedCount} object(s) selected` : `${count} object(s)`}</span>
    <div className="flex-1" />
    <span className="text-gray-700">philjustus.local</span>
  </div>
);
